import Button from "../../../custom/button/button";
import { ReactComponent as Error } from "../../../assets/Error.svg";
import PageLayout from "../../../layouts/pageLayout/pageLayout";
import { App, Modal, Spin, Table } from "antd";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getPaymentHistoryByApplicantId,
  queryProcessedPayment,
  queryProcessedPaymentWithChannel,
} from "../../../utils/request";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { paymentChannelAtom, userAtom } from "../../../utils/store";
import styles from "../dashboardPage.module.scss";
import statusStyles from "./style.module.scss";
import { formatFullDate, formattedTime } from "../../../utils/formatDate";
import { useNavigate } from "react-router-dom";
import { paymentReceiptAtom } from "../../../state";
import { formatCurrency } from "../../../utils/currency";

const Payments = () => {
  const [showModal, setShowModal] = useState(false);
  const [user, setUser] = useAtom(userAtom);
  const setPaymentData = useSetAtom(paymentReceiptAtom);
  const navigate = useNavigate();
  const channel = useAtomValue(paymentChannelAtom);

  const { notification } = App.useApp();

  const queryParams = new URLSearchParams(window.location.search);
  const transactionRef = queryParams.get("ref") ?? "";

  const PAYMENT_FAILED = "Failed";
  const PAYMENT_SUCCESSFUL = ["Successful", "200", "00"];

  const queryProcessedPaymentQuery = useQuery({
    queryKey: ["query-process-payment"],
    queryFn: () =>
      queryProcessedPaymentWithChannel({
        reference: transactionRef,
        channel: channel!,
      }),
    enabled: !!transactionRef && !!channel,
  });
  const queryProcessedPaymentData = queryProcessedPaymentQuery?.data;
  const isTransactionSuccessful = PAYMENT_SUCCESSFUL.includes(
    queryProcessedPaymentData?.transactionStatus,
  );

  const paymentHistoryQuery = useQuery({
    queryKey: ["get-payment-history"],
    queryFn: () => getPaymentHistoryByApplicantId(user?.applicantId ?? ""),
    //enabled: !!queryProcessedPaymentData,
  });

  const isQueryProcessedPaymentLoading = queryProcessedPaymentQuery?.isLoading;

  const paymentHistoryData = paymentHistoryQuery?.data?.data;
  const isPaymentHistoryLoading = paymentHistoryQuery?.isLoading;

  useEffect(() => {
    if (isTransactionSuccessful) {
      notification.success({
        message: "Success",
        description: queryProcessedPaymentData?.message,
      });
      paymentHistoryQuery.refetch();
      setUser((prev) => ({ ...prev!, isPaymentSubmitted: true }));
    } else if (
      queryProcessedPaymentData?.transactionStatus === PAYMENT_FAILED
    ) {
      notification.error({
        message: "Error",
        description: queryProcessedPaymentData?.message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryProcessedPaymentData]);

  const column = [
    {
      title: "Reference Number",
      dataIndex: "paymentReference",
      key: "paymentReference",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Program",
      key: "programName",
      render: () => <span>{user?.programName}</span>,
    },
    {
      title: "Amount Paid",
      dataIndex: "amount",
      key: "amount",
      render: (_: any, record: Payment) => (
        <span>{formatCurrency(record.amount)}</span>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (text: string) => (
        <div className={statusStyles.date}>
          <p>{formatFullDate(text)}</p>
          <p>{formattedTime(text)}</p>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (text: string) => (
        <span
          className={
            PAYMENT_SUCCESSFUL.includes(text)
              ? statusStyles.completed
              : statusStyles.pending
          }
        >
          {text}
        </span>
      ),
    },
    {
      title: "Action",
      render: (_: any, record: Payment) => (
        <span>
          <button
            className="linkButton"
            onClick={() => {
              setPaymentData(record);
              navigate(`/payment-receipt`);
            }}
          >
            View Receipt
          </button>
        </span>
      ),
    },
  ];

  if (isPaymentHistoryLoading || isQueryProcessedPaymentLoading) {
    return <Spin size="large" />;
  }

  return (
    <>
      <PageLayout
        heading="Payments"
        headerActions={
          <>
            {/* <Button
              iconBefore={<DownloadPdf />}
              text="Download"
              bgColor="#CE9938"
            /> */}
          </>
        }
      >
        <Table
          columns={column}
          dataSource={paymentHistoryData}
          className={styles.row}
          rowKey={"DueYear"}
          scroll={{ x: 400 }}
          //   pagination={{ current: currentPage, pageSize: pageSize, onChange: handlePaginationChange, position: ["bottomCenter"] }}
          pagination={false}
        />
      </PageLayout>

      <Modal
        open={showModal}
        footer=""
        onCancel={() => setShowModal(false)}
        centered
        closeIcon={false}
        className="modal"
      >
        <main className={styles.modalMain}>
          <section className={styles.modalSection}>
            <Error />

            <p>Are you sure you want to delete these details?</p>
            <span className={styles.modalButton}>
              <Button text="Cancel" />
              <Button bgColor="red" text="Delete" />
            </span>
          </section>
        </main>
      </Modal>
    </>
  );
};

export default Payments;
